import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a paginated blog posts list
 */
export default class BlogPostsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback?.();

    this._onSearchInput = this._onSearchInput.bind(this);
    this._runDebouncedSearch = this._runDebouncedSearch.bind(this);
    this._toggleRecentPanel = this._toggleRecentPanel.bind(this);
    this._syncRecentPanelMode = this._syncRecentPanelMode.bind(this);

    /** @type {HTMLInputElement | null} */
    this._searchInput = this.querySelector('[data-blog-search-input]');
    this._postsGrid =
      this.querySelector('[ref="grid"]') || this.querySelector('.blog-posts-container');
    this._recentPostsContainer = this.querySelector('.recent-posts');
    /** @type {HTMLElement | null} */
    this._blogNoResults = this.querySelector('[data-blog-no-results]');
    /** @type {HTMLElement | null} */
    this._recentNoResults = this.querySelector('[data-recent-no-results]');
    /** @type {HTMLElement | null} */
    this._blogShimmer = this.querySelector('[data-blog-shimmer]');
    /** @type {HTMLElement | null} */
    this._recentShimmer = this.querySelector('[data-recent-shimmer]');

    this._debounceMs = 500;
    this._searchTimer = null;
    this._isSearching = false;

    if (this._searchInput) {
      this._searchInput.addEventListener('input', this._onSearchInput, { passive: true });
    }

    this._indexAll();
    this._applyFilter(this._searchInput?.value ?? '');

    if (this._postsGrid) {
      this._observer = new MutationObserver(() => {
        this._indexAll();
        if (!this._isSearching) this._applyFilter(this._searchInput?.value ?? '');
      });
      this._observer.observe(this._postsGrid, { childList: true, subtree: true });
    }
    // Recent posts collapse/expand (mobile only)
    /** @type {HTMLElement | null} */
    this._recentToggle = this.querySelector('[data-recent-toggle]');
    /** @type {HTMLElement | null} */
    this._recentPanel = this.querySelector('[data-recent-panel]');
    /** @type {HTMLElement | null} */
    this._recentContainer = this.querySelector('.recent-posts-container');
    /** @type {SVGPathElement | null} */
    this._recentVerticalPath = /** @type {SVGPathElement | null} */ (
      this.querySelector('[data-recent-vertical-path]')
    );
    this._recentMql = window.matchMedia('(max-width: 749px)');

    if (this._recentToggle) {
      this._recentToggle.addEventListener('click', this._toggleRecentPanel);
      this._recentToggle.addEventListener('keydown', (evt) => {
        const keyboardEvent = /** @type {KeyboardEvent} */ (evt);
        if (keyboardEvent.key === 'Enter' || keyboardEvent.key === ' ') this._toggleRecentPanel();
      });
    }

    this._syncRecentPanelMode();
    this._recentMql?.addEventListener?.('change', this._syncRecentPanelMode);
  }

  disconnectedCallback() {
    this._observer?.disconnect();
    this._observer = null;
    if (this._searchTimer) {
      clearTimeout(this._searchTimer);
      this._searchTimer = null;
    }

    if (this._searchInput && this._onSearchInput) {
      this._searchInput.removeEventListener('input', this._onSearchInput);
    }

    this._searchInput = null;
    this._postsGrid = null;
    this._recentPostsContainer = null;
    this._blogNoResults = null;
    this._recentNoResults = null;
    this._blogShimmer = null;
    this._recentShimmer = null;
    this._postIndex = undefined;
    this._recentIndex = undefined;
    this._isSearching = false;

    super.disconnectedCallback?.();
    // Recent posts collapse/expand cleanup
    this._recentMql?.removeEventListener?.('change', this._syncRecentPanelMode);
    this._recentToggle = null;
    this._recentPanel = null;
    this._recentContainer = null;
    this._recentVerticalPath = null;
    this._recentMql = null;
  }
_syncRecentPanelMode() {
    const isMobile = this._recentMql?.matches ?? false;
    if (!isMobile) {
      if (this._recentContainer) {
        const container = /** @type {HTMLElement} */ (this._recentContainer);
        container.dataset.collapsed = 'false';
      }
      if (this._recentToggle) this._recentToggle.setAttribute('aria-expanded', 'true');
      if (this._recentVerticalPath) this._recentVerticalPath.style.opacity = '0';
      return;
    }

    // On mobile, keep current collapsed state (default expanded).
    if (this._recentContainer) {
      const container = /** @type {HTMLElement} */ (this._recentContainer);
      if (!container.dataset.collapsed) container.dataset.collapsed = 'false';
    }

    const container = this._recentContainer ? /** @type {HTMLElement} */ (this._recentContainer) : null;
    const collapsed = container?.dataset?.collapsed === 'true';
    if (this._recentToggle) this._recentToggle.setAttribute('aria-expanded', String(!collapsed));
    if (this._recentVerticalPath) this._recentVerticalPath.style.opacity = collapsed ? '1' : '0';
  }

  _toggleRecentPanel() {
    const isMobile = this._recentMql?.matches ?? false;
    if (!isMobile) return;
    if (!this._recentContainer) return;

    const container = /** @type {HTMLElement} */ (this._recentContainer);
    const collapsed = container.dataset.collapsed === 'true';
    const newCollapsed = collapsed ? 'false' : 'true';
    container.dataset.collapsed = newCollapsed;

    if (this._recentToggle) {
      this._recentToggle.setAttribute('aria-expanded', String(newCollapsed !== 'true'));
    }
    if (this._recentVerticalPath) {
      this._recentVerticalPath.style.opacity = newCollapsed === 'true' ? '1' : '0';
    }
  }
  /** @param {Event} e */
  _onSearchInput(e) {
    const target = /** @type {HTMLInputElement | null} */ (e?.target ?? null);
    const value = target?.value ?? '';

    if (this._searchTimer) clearTimeout(this._searchTimer);
    this._setSearching(true);
    this._searchTimer = setTimeout(() => this._runDebouncedSearch(value), this._debounceMs);
  }

  /** @param {string} value */
  _runDebouncedSearch(value) {
    this._indexAll();
    this._applyFilter(value);
    this._setSearching(false);
  }

  /** @param {boolean} isSearching */
  _setSearching(isSearching) {
    this._isSearching = isSearching;
    const active = Boolean((this._searchInput?.value ?? '').trim());

    // Toggle shimmer visibility (visible only while we are actively "searching")
    if (this._blogShimmer) this._blogShimmer.hidden = !(active && isSearching);
    if (this._recentShimmer) this._recentShimmer.hidden = !(active && isSearching);

    // Main blog cards: hide grid while shimmering, show it otherwise.
    if (this._postsGrid && this._blogShimmer) {
      const grid = /** @type {HTMLElement} */ (this._postsGrid);
      if (active && isSearching) {
        grid.style.display = 'none';
      } else {
        if (!active) {
          // recent.style.display = '';
          grid.style.display = '';
        }
      }
    }

    // Recent posts list: only hide the real list while shimmering.
    if (this._recentPostsContainer && this._recentShimmer) {
      const recent = /** @type {HTMLElement} */ (this._recentPostsContainer);
      if (active && isSearching) {
        recent.style.display = 'none';
      } else {
        // Let _applyFilter decide if it should be hidden for "no results";
        // only force-show when not searching.
        if (!active) {
          recent.style.display = '';
        }
      }
    }

    // Hide "no results" while shimmering (it will be recalculated after filtering)
    if (isSearching) {
      if (this._blogNoResults) this._blogNoResults.hidden = true;
      if (this._recentNoResults) this._recentNoResults.hidden = true;
    }
  }

  _indexAll() {
    /** @type {Map<HTMLElement, string>} */
    const postIndex = (this._postIndex = new Map());
    this.querySelectorAll('[ref="cards[]"]').forEach((el) => {
      /** @type {HTMLElement} */
      // @ts-expect-error - Shopify theme TS checks on JS
      const card = el;
      const text = (el.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
      postIndex.set(card, text);
    });

    /** @type {Map<HTMLElement, string>} */
    const recentIndex = (this._recentIndex = new Map());
    this.querySelectorAll('.recent-post').forEach((el) => {
      /** @type {HTMLElement} */
      // @ts-expect-error - Shopify theme TS checks on JS
      const item = el;
      const text = (el.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
      recentIndex.set(item, text);
    });
  }

  /** @param {string} rawQuery */
  _applyFilter(rawQuery) {
    const q = String(rawQuery || '').trim().toLowerCase();
    const active = q.length > 0;

    // if (!this._postIndex) this._indexAll();
    if (this._postIndex) {
      // Main posts grid
      let anyBlogVisible = false;
      for (const [el, haystack] of this._postIndex?.entries() ?? []) {
        const match = !active || haystack.includes(q);
        el.style.display = match ? '' : 'none';
        anyBlogVisible ||= match;
      }
      if (this._blogNoResults) {
        this._blogNoResults.hidden = !active || anyBlogVisible;
      }
      if (this._postsGrid) {
        const container = /** @type {HTMLElement} */ (this._postsGrid);
        container.style.display = active && !anyBlogVisible ? 'none' : '';
      }
    }
    // Sidebar recent posts (optional)
    if (this._recentIndex) {
      let anyRecentVisible = false;
      for (const [el, haystack] of this._recentIndex?.entries() ?? []) {
        const match = !active || haystack.includes(q);
        el.style.display = match ? '' : 'none';
        anyRecentVisible ||= match;
      }
      if (this._recentNoResults) {
        this._recentNoResults.hidden = !active || anyRecentVisible;
      }
      if (this._recentPostsContainer) {
        const container = /** @type {HTMLElement} */ (this._recentPostsContainer);
        container.style.display = active && !anyRecentVisible ? 'none' : '';
      }
    }
  }
}

if (!customElements.get('blog-posts-list')) {
  customElements.define('blog-posts-list', BlogPostsList);
}
